import { TreeSelect, message, Tooltip } from "antd";
import dayjs from "dayjs";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { expandCategories } from "../../../data/slices/table";
import {
  DECALAGE_SERIE,
  DUPLICATE_PERIOD_COMPTA,
  DUPLICATE_PERIOD_TRESO,
  ONLY_SIMUL,
  TRESORY_STATUS,
  WRITING_TYPES,
} from "../../../data/constants";
import { getAccounts } from "../../../data/slices/accounts";
import { getCategories } from "../../../data/slices/categories";
import {
  createElements,
  editElement,
  editElementSerie,
  openReellePopup,
  ventilateElement,
} from "../../../data/slices/elements";
import { closeModal, openModal } from "../../../data/slices/modals";
import { useFiltersAndDates } from "../../../hooks/useFiltersAndDate";
import { daysList } from "../../../utilities/daysList";
import {
  getLabelForDateDifference,
  getLastDayLabel,
  handleChangeComptaDate,
  handleChangeTresoDate,
} from "../../../utilities/duplication";
import { convertToYYYYMMDD } from "../../../utilities/filters";
import formattedCategories, {
  formatPlanCompta,
  getCategoryAncestorKeys,
} from "../../../utilities/formattedCategories";
import { checkIfIsLocked , filterUnmaskedCategories} from "../../../utilities/tableUtils";
import getChangedValues from "../../../utilities/getChangedValues";
import { getFieldClasses } from "../../../utilities/getFieldClasses";
import { isZero } from "../../../utilities/isZero";
import {
  evaluateAmountExpression,
  formatResolvedAmount,
  resolveAmountValue,
  roundAmount,
} from "../../../utilities/evaluateAmountExpression";
import { displayValidationErrors } from "../../../utilities/noticeDisplayer";
import { findById } from "../../../utilities/table";
import {
  getDispalyedValueCompta,
  getDisplayValueTreso,
  getInitialValueDate,
  getStatus,
} from "../../../utilities/writingForm";
import Button from "../../Button";
import CustomDatePicker from "../../CustomDatePicker";
import CustomDateSelect from "../../CustomDateSelect";
import CustomSelect from "../../CustomSelect";
import CustomDropdown from "../../CustomSelector";
import CustomInput from "../../Input";
import { getPlancompta } from "../../../data/slices/plancompta";
import link from "../../../assets/img/icons/table/linking.svg";
import duplicateWriting from "../../../assets/img/duplicate.png";
import {
  categorizeImportedElementsWithAI,
  moveImportedElementToCategory,
  updateImportedElementLabel,
} from "../../../data/slices/importedElements";
import { LoadingOutlined } from "@ant-design/icons";

const AddWritingForm = ({
  onCancel,
  edit,
  element,
  originalCategoryId,
  selectedCategory,
  projectId,
  isAction,
  date,
  handleInputFocus,
  emptyDateTreso,
  emptyDateCompta,
  type,
  action,
  dateTresoDndSpecial,
  dateComptaDndSpecial,
  isCreateByDuplication,
  isImported,
  isImportedAfterDnd,
  aiCategorization,
  isVentilation,
}) => {
  const dispatch = useDispatch();
  const { objectif } = useSelector((state) => state.objectives);
  const location = useLocation();
  const [submitButtonState, setSubmitButtonState] = useState(null);
  const submitButtonStateRef = useRef(null);
  const { constructFilters, getStartAndEndDate, getStartAndEndDateSynthesis } =
    useFiltersAndDates();
  const [isSpecial, isSpecialState] = useState();
  const aiNextCancelledRef = useRef(false);
  // Force TreeSelect remount per writing so expanded branches from a previous
  // "Valider et classer l'écriture suivante" do not stick around.
  const [categoryTreeInstanceKey, setCategoryTreeInstanceKey] = useState(0);
  const [categoryTreeSearchValue, setCategoryTreeSearchValue] = useState("");
  const [categoryTreeExpandedKeys, setCategoryTreeExpandedKeys] = useState([]);

  const { footerConstructedFilters, tableConstructedFilters } =
    constructFilters();

  const { startDate, endDate } = getStartAndEndDate();
  const { startDateSynthesis, endDateSynthesis } =
    getStartAndEndDateSynthesis();

  const { mode } = useSelector((state) => state.layout);
  const { reellePopup } = useSelector((state) => state.elements);
  const { project } = useSelector((state) => state.project);
  const { importedElements } = useSelector((state) => state.importedElements);

  const { categories, categoriesForSelect } = useSelector(
    (state) => state.categories
  );

  useEffect(() => {
    // Reset cancellation flag whenever we load a new imported element
    aiNextCancelledRef.current = false;
    if (isImported) {
      setCategoryTreeInstanceKey((key) => key + 1);
      setCategoryTreeSearchValue("");
    }
  }, [element?.id, isImported]);

  const { plancomptaForSelect } = useSelector((state) => state.plancompta);

  const { expandedCategories } = useSelector((state) => state.table);
  const { accounts } = useSelector((state) => state.accounts);

  // Never seed ventilation from reellePopup: it often still holds the category
  // from the previous "Valider et ventiler" (e.g. A) and would incorrectly
  // win when the lookup for the new category (B) races with a categories refresh.
  let currentCategory =
    isImported || isVentilation ? null : reellePopup?.category;

  if (
    currentCategory &&
    typeof currentCategory.id === "string" &&
    currentCategory.id.includes("-racine") &&
    categoriesForSelect?.length
  ) {
    const baseId = currentCategory.id.split("-")[0];
    const normalizedId = Number(baseId);

    if (!Number.isNaN(normalizedId)) {
      const baseCategory = findById(normalizedId, categoriesForSelect);
      if (baseCategory) {
        currentCategory = baseCategory;
      }
    }
  }

  // Suggested category from AI (for imported elements classification)
  let suggestedCategoryId = null;
  if (
    isImported &&
    aiCategorization &&
    Array.isArray(aiCategorization) &&
    element?.id
  ) {
    const match = aiCategorization.find(
      (item) =>
        String(item.transactionId) === String(element.id) &&
        item.categoryId !== undefined &&
        item.categoryId !== null
    );
    if (match) {
      const parsed = Number(match.categoryId);
      if (!Number.isNaN(parsed)) {
        suggestedCategoryId = parsed;
      }
    }
  }

  if (isImported && suggestedCategoryId && categoriesForSelect?.length) {
    const aiCategory = findById(suggestedCategoryId, categoriesForSelect);
    if (aiCategory) {
      currentCategory = aiCategory;
    }
  }

  // Resolve category fully before building TreeSelect options. Otherwise the
  // select can keep a raw id (e.g. 3909) when the matching node is missing
  // from the filtered tree — common after "Valider et ventiler" from AI.
  if (!currentCategory && element?.categoryId && categoriesForSelect?.length) {
    currentCategory = findById(element.categoryId, categoriesForSelect);
  }

  if (isImportedAfterDnd) {
    currentCategory = findById(selectedCategory, categoriesForSelect);
  }

  if (
    isImported &&
    !currentCategory &&
    selectedCategory &&
    categoriesForSelect?.length
  ) {
    currentCategory = findById(selectedCategory, categoriesForSelect);
  }

  if (isVentilation) {
    const targetCategoryId = element?.categoryId || selectedCategory;
    const categoryFromId =
      targetCategoryId != null && categoriesForSelect?.length
        ? findById(targetCategoryId, categoriesForSelect)
        : null;
    const categoryFromElement =
      element?.category &&
      typeof element.category === "object" &&
      element.category.name &&
      (targetCategoryId == null ||
        String(element.category.id) === String(targetCategoryId))
        ? element.category
        : null;
    currentCategory = categoryFromId || categoryFromElement || null;
  }

  const categoryId = currentCategory?.id;

  let categoriesForSelectToUse = filterUnmaskedCategories(
    categoriesForSelect,
    categoryId
  );

  useEffect(() => {
    if (!isImported) {
      return;
    }
    setCategoryTreeExpandedKeys(
      getCategoryAncestorKeys(categoriesForSelect, categoryId)
    );
  }, [isImported, element?.id, categoryId, categoriesForSelect]);

  const isElementLinked = element?.linkedGroupId;

  if (
    !element?.isImported &&
    !isImportedAfterDnd &&
    !currentCategory &&
    isAction &&
    type === "simulation"
  ) {
    categoriesForSelectToUse = categoriesForSelect.filter(
      (el) => el?.isSpecial !== 1
    );
  }

  const TRESO_MODE = mode === "treso";
  const COMPTA_MODE = mode === "compta";

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(getCategories({ projectId: projectId, mode: mode }));
    }
  }, [projectId, mode, categories.length]);

  useEffect(() => {
    dispatch(getAccounts(projectId));
  }, [projectId, mode]);

  useEffect(() => {
    // Always fetch plan comptable when projectId changes to ensure fresh data for the current project
    dispatch(getPlancompta({ projectId }));
  }, [projectId, mode, dispatch]);

  const getDateValidation = (isSpecialMode, isSpecialField) => {
    if ((isSpecialMode && isSpecialField) || !isSpecialField) {
      return Yup.string().required("Date à saisir");
    }
    return Yup.string().nullable().optional();
  };

  const validateDateNotLocked = (date, mode, fieldName) => {
    if (!date || !project) return true;

    try {
      if (edit && !isCreateByDuplication) {
        let oldDate;
        if (mode === "treso") {
          oldDate = element?.dateTreso;
        } else {
          oldDate = element?.dateCompta;
        }

        if (oldDate) {
          const oldIsoDate = dayjs(oldDate).toISOString();
          const oldMockElement =
            mode === "treso"
              ? { dateTreso: oldIsoDate }
              : { dateCompta: oldIsoDate };
          const oldIsLocked = checkIfIsLocked(oldMockElement, mode, project);

          if (oldIsLocked) {
            return true;
          }
        }
      }

      const isoDate = dayjs(date).toISOString();
      const mockElement =
        mode === "treso" ? { dateTreso: isoDate } : { dateCompta: isoDate };

      const isLocked = checkIfIsLocked(mockElement, mode, project);
      return !isLocked;
    } catch (error) {
      console.error("Error validating lock date:", error);
      return true;
    }
  };

  const WritingSchema = Yup.object().shape({
    designation: Yup.string().required("Libellé à saisir"),
    amount: Yup.string()
      .required("Montant à saisir")
      .test("valid-amount-expression", "Montant invalide", (value) => {
        return evaluateAmountExpression(value).ok;
      }),
    categoryId: Yup.string().required("Catégorie à sélectionner"),
    type: Yup.string().required("Type est obligatoire"),
    status: Yup.string().required("Statut est obligatoire"),
    dateCompta: element?.isImported
      ? Yup.string()
          .nullable()
          .test(
            "required-if-not-special",
            "Date d'engagement à saisir",
            function (value) {
              const { isSpecial } = this.parent;
              return isSpecial ? true : !!value;
            }
          )
      : getDateValidation(COMPTA_MODE, isSpecial).test(
          "not-locked",
          "Date non valide car elle correspond à une période verrouillée",
          function (value) {
            if (isVentilation) return true;
            return validateDateNotLocked(value, "compta", "dateCompta");
          }
        ),
    dateTreso: getDateValidation(TRESO_MODE, isSpecial).test(
      "not-locked",
      "Date non valide car elle correspond à une période verrouillée",
      function (value) {
        if (isVentilation) return true;
        return validateDateNotLocked(value, "treso", "dateTreso");
      }
    ),
    ...(TRESO_MODE && {
      accountTresoId: Yup.mixed().required("compte à saisir"),
    }),
    planComptableId:
      isSpecial === 0 && !TRESO_MODE
        ? Yup.string().required("plan comptable à saisir")
        : Yup.string().optional().nullable(),
  });

  const customDate = dayjs(convertToYYYYMMDD(date));
  const currentDate = dayjs();

  const sameMonthAndYear =
    customDate.month() === currentDate.month() &&
    customDate.year() === currentDate.year();

  const existingDateTreso = dateTresoDndSpecial || element?.dateTreso;
  const existingDateCompta = dateComptaDndSpecial || element?.dateCompta;

  const isDuplicated = !!element?.isDuplicated;

  const [selectedDateTreso, setSelectedDateTreso] = useState(existingDateTreso);

  const [selectedDateCompta, setSelectedDateCompta] =
    useState(existingDateCompta);

  const [displayValueTreso, setDisplayValueTreso] = useState(null);
  const [displayValueCompta, setDisplayValueCompta] = useState(null);
  const [displayDecalageValue, setDisplayDecalageValue] = useState(null);

  const optionsTreso = TRESO_MODE
    ? daysList(existingDateTreso)
    : DUPLICATE_PERIOD_COMPTA;

  const optionsCompta = TRESO_MODE
    ? DUPLICATE_PERIOD_TRESO
    : daysList(existingDateCompta);

  const statusInitialValue = useMemo(() => {
    const isSpecial = currentCategory?.isSpecial;

    return getStatus(edit, element, COMPTA_MODE, isSpecial);
  }, [edit, element?.status, mode, currentCategory?.isSpecial]);

  let typeInitialValue = null;
  if (type === "simulation") {
    typeInitialValue = "simulation";
  } else if (isVentilation) {
    typeInitialValue = element?.type || "réel";
  } else if (edit) {
    typeInitialValue = element.type;
  } else {
    typeInitialValue = "réel";
  }

  let comptaDateInitialValue = getInitialValueDate(
    COMPTA_MODE,
    edit,
    existingDateCompta,
    date,
    sameMonthAndYear,
    isAction
  );

  if (isVentilation && existingDateCompta) {
    comptaDateInitialValue = existingDateCompta;
  }

  let tresoDateInitialValue = getInitialValueDate(
    TRESO_MODE,
    edit,
    existingDateTreso,
    date,
    sameMonthAndYear,
    isAction
  );

  if (isVentilation && existingDateTreso) {
    tresoDateInitialValue = existingDateTreso;
  }

  const principalAccount = accounts.find((el) => el?.type === "PRINCIPAL");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const defaultPlanId = useMemo(() => {
    const defaultPlan = plancomptaForSelect.find(
      (plan) => plan.name === "A définir" && Number(plan.index) === 0
    );
    return normalizeId(defaultPlan?.id);
  }, [plancomptaForSelect]);

  const hasExplicitPlan = (planId) => {
    const normalized = normalizeId(planId);
    return normalized !== null && (!defaultPlanId || normalized !== defaultPlanId);
  };

  let plancomptaId = currentCategory?.planComptableId;
  if (reellePopup?.isPlancompta) {
    plancomptaId = reellePopup?.planComptaId?.id;
  }
  if (edit) {
    plancomptaId = element?.planComptableId;
  }

  const getPlanComptableIdToUse = () => {
    // Priority 1: Element's explicit plan comptable always takes precedence
    if (edit && element?.planComptableId !== null && element?.planComptableId !== undefined) {
      return normalizeId(element?.planComptableId);
    }

    if (hasExplicitPlan(element?.planComptableId)) {
      return normalizeId(element?.planComptableId);
    }

    if (isImported) {
      if (hasExplicitPlan(plancomptaId)) {
        return normalizeId(plancomptaId);
      }

      // Priority 2: Category's plan comptable (only if element doesn't have explicit plan)
      if (hasExplicitPlan(currentCategory?.planComptableId)) {
        return normalizeId(currentCategory?.planComptableId);
      }

      return (
        normalizeId(plancomptaId) ??
        normalizeId(currentCategory?.planComptableId) ??
        defaultPlanId
      );
    }

    // Priority 2: Category's plan comptable (only if element doesn't have explicit plan)
    if (hasExplicitPlan(currentCategory?.planComptableId)) {
      return normalizeId(currentCategory?.planComptableId);
    }

    if (hasExplicitPlan(plancomptaId)) {
      return normalizeId(plancomptaId);
    }

    // Priority 3: Default "A définir"
    return (
      defaultPlanId ??
      normalizeId(plancomptaId) ??
      normalizeId(currentCategory?.planComptableId)
    );
  };

  const finalPlancomptaId = getPlanComptableIdToUse();

  let plancomptaForSelectToUse = filterUnmaskedCategories(
    plancomptaForSelect,
    finalPlancomptaId?.toString()
  );

  const finalCategories = formattedCategories(categoriesForSelectToUse);
  const finalPlancompta = formatPlanCompta(
    plancomptaForSelectToUse,
    COMPTA_MODE && currentCategory?.isSpecial
  );

  const initialValues = useMemo(
    () => ({
      isSpecial: currentCategory?.isSpecial || false,
      designation: edit || isVentilation ? element?.designation : null,
      amount: isVentilation ? null : edit ? element?.amount?.toString() : null,
      categoryId: currentCategory ? currentCategory?.id : null,
      planComptableId: finalPlancomptaId,
      dateCompta: emptyDateCompta ? "" : comptaDateInitialValue,
      dateTreso: emptyDateTreso ? "" : tresoDateInitialValue,
      type: isVentilation ? element?.type : typeInitialValue,
      status: isVentilation ? element?.status : statusInitialValue,
      decalageDate: null,
      accountColor: edit || isVentilation
        ? accounts.find((el) => el.id === element?.accountTresoId?.toString())
            ?.color
        : principalAccount?.color,
      accountTresoId:
        edit || isVentilation ? element?.accountTresoId : principalAccount?.id,
      accountTresoLabel: edit || isVentilation
        ? accounts.find((el) => el.id === element?.accountTresoId?.toString())
            ?.name
        : principalAccount?.name,
      accountCode: edit || isVentilation
        ? accounts.find((el) => el.id === element?.accountTresoId?.toString())
            ?.code
        : principalAccount?.code,
    }),
    [
      currentCategory,
      edit,
      element?.designation,
      element?.amount,
      element?.status,
      element?.type,
      element?.accountTresoId,
      comptaDateInitialValue,
      tresoDateInitialValue,
      typeInitialValue,
      statusInitialValue,
      TRESO_MODE,
      principalAccount?.id,
      principalAccount?.name,
      principalAccount?.code,
      accounts,
      isImportedAfterDnd,
      finalPlancomptaId,
      isVentilation,
    ]
  );

  const handleSubmitButtonState = (isSubmit) => {
    submitButtonStateRef.current = isSubmit;
    setSubmitButtonState(isSubmit);
  };

  const handelChangeCategories = (setFieldValue, value, currentValues) => {
    if (value) {
      setFieldValue("categoryId", value);
      const categoryToSend = findById(value, categoriesForSelect);
      const isSpecial = categoryToSend?.isSpecial;
      setFieldValue("isSpecial", isSpecial);
      if (isAction && COMPTA_MODE && isSpecial) {
        setFieldValue("status", "pointé");
      }
      
      // For imported elements only: every category change must trigger plan comptable check and assignment
      if (isImported) {
        // Check if category has an assigned plan comptable different from "A définir"
        if (hasExplicitPlan(categoryToSend?.planComptableId)) {
          // Category has an assigned plan comptable, use it
          setFieldValue("planComptableId", categoryToSend.planComptableId);
        } else {
          // Category doesn't have an assigned plan or it's "A définir", use default
          setFieldValue("planComptableId", defaultPlanId ?? null);
        }
      } else if (COMPTA_MODE) {
        // Original behavior for non-imported elements in COMPTA_MODE
        // Only update plan comptable if element doesn't already have an explicit plan
        const currentPlanId = currentValues?.planComptableId ?? element?.planComptableId;
        if (!hasExplicitPlan(currentPlanId)) {
          // Element has no explicit plan, so use category's plan or default
          if (hasExplicitPlan(categoryToSend?.planComptableId)) {
            setFieldValue("planComptableId", categoryToSend.planComptableId);
          } else {
            setFieldValue("planComptableId", defaultPlanId ?? null);
          }
        }
        // If element has explicit plan, keep it (don't override)
      }
      
      return;
    }

    // Category cleared
    setFieldValue("categoryId", null);
    if (isImported || COMPTA_MODE) {
      // For imported elements or COMPTA mode, reset/clear to default "A definir"
      const currentPlanId = currentValues?.planComptableId ?? element?.planComptableId;
      // Only reset/clear plan if element doesn't have an explicit plan
      if (!hasExplicitPlan(currentPlanId)) {
        setFieldValue("planComptableId", defaultPlanId ?? null);
      }
    }
  };

  const getDatesToSend = (values, categoryToSend) => {
    if (categoryToSend.isSpecial === 0) {
      return {
        dateCompta: values.dateCompta,
        dateTreso: values?.dateTreso,
      };
    }
    return COMPTA_MODE
      ? { dateCompta: values.dateCompta, dateTreso: null }
      : { dateTreso: values?.dateTreso, dateCompta: null };
  };

  const getProjectConfig = () => ({
    mode,
    projectId,
    params: { startDate, endDate, ...tableConstructedFilters },
  });

  const getCommonConfig = () => {
    const isPlancomptaView = location.pathname.includes("compta-plan") || reellePopup?.isPlancompta;
    
    return {
      mode,
      objectiveId: objectif?.id,
      objective: {
        objectiveId: objectif?.id,
        isPlancompta: isPlancomptaView,
      },
      project: getProjectConfig(),
      isListing: location.pathname.includes("listing"),
      listing: {
        params: {
          ...tableConstructedFilters,
          startDate,
          endDate,
        },
        mode,
        projectId,
      },
      isPlancompta: isPlancomptaView,
      sum: {
        mode,
        projectId,
        params: {
          startDate,
          endDate,
          ...footerConstructedFilters,
        },
      },
      synthesis: {
        mode,
        projectId,
        params: {
          startDate: startDateSynthesis,
          endDate: endDateSynthesis,
          ...tableConstructedFilters,
        },
      },
    };
  };

  const handleSaveImportedLabel = async (values, setSubmitting) => {
    const nextDesignation = String(values?.designation || "").trim();
    if (!nextDesignation) {
      message.error("Libellé à saisir");
      return;
    }
    if (nextDesignation === String(element?.designation || "").trim()) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await dispatch(
        updateImportedElementLabel({
          elementId: element?.id,
          designation: nextDesignation,
          values: getCommonConfig(),
        })
      );
      if (updateImportedElementLabel.rejected.match(result)) {
        displayValidationErrors(result?.payload || result?.error);
        return;
      }
      message.success("Libellé enregistré");
      dispatch(
        openModal("add-writing-expression", {
          edit: true,
          element: {
            ...element,
            designation: nextDesignation,
            isImported: true,
            isSpecial: true,
          },
          selectedCategory,
          category: element?.category,
          projectId,
          isImported: true,
          date: element?.dateTreso || date,
          aiCategorization,
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmission = async (values, setSubmitting, resetForm) => {
    const shouldGoToNextImported =
      submitButtonStateRef.current === "validateAndNext" && element?.isImported;
    const shouldOpenVentilation =
      submitButtonStateRef.current === "validateAndVentilate" &&
      element?.isImported;

    let nextImportedElement = null;

    if (shouldGoToNextImported && Array.isArray(importedElements)) {
      const currentIndex = importedElements.findIndex(
        (el) => String(el.id) === String(element?.id)
      );

      if (currentIndex !== -1 && importedElements[currentIndex + 1]) {
        nextImportedElement = importedElements[currentIndex + 1];
      }
    }

    const dataState = buildDataState(values);
    const editBody = buildEditBody(dataState);

    const baselineInitialValues =
      originalCategoryId && isDuplicated && type === "all"
        ? {
            ...initialValues,
            categoryId: originalCategoryId,
          }
        : initialValues;

    const fieldUpdated = getChangedValues(editBody.values, baselineInitialValues);

    if (isDuplicated && Object.keys(fieldUpdated).length === 0) {
      message.error(
        "Aucune modification détectée. Veuillez changer au moins un champ avant de valider."
      );
      return;
    }

    if (isImportedAfterDnd) {
      action({
        designation: values?.designation,
        categoryId: values?.categoryId,
        dateCompta: values?.dateCompta,
        planComptableId: values?.planComptableId,
      });
      setSubmitting(false);
      resetForm();
      return;
    }

    const result = await dispatchEdit(editBody, fieldUpdated);

    setSubmitting(false);
    resetForm();

    if (result.meta.requestStatus === "rejected") {
      displayValidationErrors(result?.errors || result?.payload);
      return;
    }

    message.success(getSuccessMessage());

    if (shouldOpenVentilation) {
      const createdElement = result?.payload?.data;
      if (!createdElement?.id) {
        displayValidationErrors({
          message:
            "Écriture classée, mais impossible d'ouvrir la ventilation automatiquement",
        });
        handleCategoryExpansion(values);
        dispatch(closeModal("add-writing-expression"));
        return;
      }

      const targetCategoryId =
        values.categoryId || createdElement?.categoryId;
      const classifiedCategory =
        findById(targetCategoryId, categoriesForSelect) ||
        findById(createdElement?.categoryId, categoriesForSelect) ||
        (createdElement?.category &&
        typeof createdElement.category === "object" &&
        createdElement.category.name
          ? createdElement.category
          : null) ||
        (targetCategoryId != null ? { id: targetCategoryId } : null);

      handleCategoryExpansion(values);
      // Always overwrite leftover reellePopup from a previous ventilation.
      dispatch(
        openReellePopup(
          classifiedCategory ? { category: classifiedCategory } : null
        )
      );
      dispatch(
        openModal("add-writing-expression", {
          element: {
            ...createdElement,
            categoryId: targetCategoryId,
            category: classifiedCategory,
            designation:
              values?.designation ||
              createdElement?.designation ||
              element?.designation,
            amount: createdElement?.amount ?? element?.amount,
            dateTreso:
              createdElement?.dateTreso ||
              values?.dateTreso ||
              element?.dateTreso,
            dateCompta:
              createdElement?.dateCompta ||
              values?.dateCompta ||
              element?.dateCompta,
            type: createdElement?.type || values?.type || element?.type,
            status: createdElement?.status || values?.status || element?.status,
            accountTresoId:
              createdElement?.accountTresoId ||
              values?.accountTresoId ||
              element?.accountTresoId,
            planComptableId:
              createdElement?.planComptableId ||
              values?.planComptableId ||
              element?.planComptableId,
          },
          selectedCategory: targetCategoryId,
          projectId,
          date:
            createdElement?.dateTreso ||
            values?.dateTreso ||
            element?.dateTreso ||
            date,
          isVentilation: true,
        })
      );
      return;
    }

    if (shouldGoToNextImported) {
      if (!nextImportedElement) {
        dispatch(closeModal("add-writing-expression"));
        return;
      }

      try {
        // Immediately open the modal with the next imported element data
        // so the user does not keep seeing the previous one.
        dispatch(
          openModal("add-writing-expression", {
            edit: true,
            element: {
              ...nextImportedElement,
              isImported: true,
              isSpecial: true,
            },
            selectedCategory,
            category: nextImportedElement?.category,
            projectId,
            isImported: true,
            date: nextImportedElement?.dateTreso,
          })
        );

        // If the current flow was triggered from "Classer avec l'aide de l'IA",
        // fetch AI suggestion for the next element in the background.
        if (aiCategorization) {
          const loadingKey = "ai-next-imported-categorization";
          message.open({
            icon: null,
            key: loadingKey,
            duration: 0,
            content: (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <LoadingOutlined spin style={{ fontSize: 16 }} />
                  <span>
                    Classement avec l'aide de l'IA en cours ...
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    aiNextCancelledRef.current = true;
                    message.destroy(loadingKey);
                  }}
                  style={{
                    borderRadius: 16,
                    border: "1px solid #000",
                    background: "#fff",
                    cursor: "pointer",
                    fontSize: 12,
                    padding: "2px 10px",
                    lineHeight: 1.4,
                  }}
                >
                  Annuler
                </button>
              </div>
            ),
          });

          const categorizeRes = await dispatch(
            categorizeImportedElementsWithAI({
              projectId,
              elementId: nextImportedElement.id,
            })
          );

          // If the user cancelled the popup while we were waiting, stop here
          if (aiNextCancelledRef.current) {
            message.destroy(loadingKey);
            return;
          }

          if (categorizeRes.meta.requestStatus === "rejected") {
            message.error({
              content:
                "Échec de la proposition de catégorie pour l'écriture suivante.",
              key: loadingKey,
            });
            displayValidationErrors(
              categorizeRes.payload || categorizeRes.error
            );
            return;
          }

          // Re-open the modal with the AI suggestion for the next element.
          // This will refresh the category suggestion once the API responds.
          message.success({
            content:
              "Proposition de catégorie pour l'écriture suivante obtenue.",
            key: loadingKey,
          });

          dispatch(
            openModal("add-writing-expression", {
              edit: true,
              element: {
                ...nextImportedElement,
                isImported: true,
                isSpecial: true,
              },
              selectedCategory,
              category: nextImportedElement?.category,
              projectId,
              isImported: true,
              date: nextImportedElement?.dateTreso,
              aiCategorization: categorizeRes.payload,
            })
          );
        }
      } catch (error) {
        console.error("Error while opening next imported element:", error);
        dispatch(closeModal("add-writing-expression"));
      }

      return;
    }

    handleCategoryExpansion(values);
    handleModalClose();
  };

  const buildDataState = (values) => {
    const resolvedAmount = resolveAmountValue(values?.amount);
    const dataState = {
      designation: values.designation,
      amount: resolvedAmount !== null ? String(resolvedAmount) : values?.amount?.toString(),
      dateCompta: values?.dateCompta,
      dateTreso: values?.dateTreso,
      type: values.type,
      status: values.status,
      accountTresoId: Number(values.accountTresoId),
      categoryId: Number(values.categoryId),
    };

    const isSpecialTresoCategory = values.isSpecial && TRESO_MODE;

    // For imported elements, use dateComptable instead of dateCompta
    if (element?.isImported && values?.dateCompta && !isSpecialTresoCategory) {
      dataState.dateComptable = values.dateCompta;
      delete dataState.dateCompta;
    }

    // Include planComptableId if:
    // 1. Not a special treso category
    // 2. Plan comptable is set (not null/undefined)
    // 3. And either:
    //    - Element is imported, OR
    //    - Plan comptable differs from original element's plan (compare against element, not initialValues)
    if (!isSpecialTresoCategory && values.planComptableId !== null && values.planComptableId !== undefined) {
      const elementOriginalPlanId = normalizeId(element?.planComptableId);
      const currentPlanId = normalizeId(values.planComptableId);
      
      if (element?.isImported || elementOriginalPlanId !== currentPlanId) {
        dataState.planComptableId = Number(values.planComptableId);
      }
    }

    // For series edit: when either series shift or date offset changes, always send both
    if (isDuplicated && type === "all") {
      let decalageSerie = values.decalageSerie;
      let decalageDate = values.decalageDate;
      const decalageDay = values.decalageDay;

      const hasSerieChange =
        decalageSerie !== undefined && decalageSerie !== null;
      const hasDateChange =
        decalageDate !== undefined && decalageDate !== null;

      if (hasSerieChange || hasDateChange) {
        if (!hasDateChange) {
          if (displayValueCompta !== null && TRESO_MODE) {
            decalageDate = (-parseFloat(displayValueCompta)).toString();
          } else if (displayValueTreso !== null && COMPTA_MODE) {
            decalageDate = displayValueTreso.toString();
          } else if (values.dateTreso && values.dateCompta) {
            const monthDiff = dayjs(values.dateTreso).diff(
              dayjs(values.dateCompta),
              "month"
            );
            // TRESO: dateCompta = dateTreso + decalageDate
            // COMPTA: dateTreso = dateCompta + decalageDate
            const derived = String(TRESO_MODE ? -monthDiff : monthDiff);
            if (["-2", "-1", "0", "1", "2"].includes(derived)) {
              decalageDate = derived;
            }
          }
        }

        if (
          !hasSerieChange &&
          displayDecalageValue !== null &&
          !isZero(displayDecalageValue)
        ) {
          decalageSerie = displayDecalageValue.toString();
        }

        if (decalageSerie !== undefined && decalageSerie !== null) {
          dataState.decalageSerie = decalageSerie;
        }
        if (decalageDate !== undefined && decalageDate !== null) {
          dataState.decalageDate = decalageDate;
        }
      }

      if (decalageDay !== undefined && decalageDay !== null) {
        dataState.decalageDay = decalageDay;
      }
    }

    return dataState;
  };

  const buildEditBody = (dataState) => ({
    elementId: element?.id,
    values: dataState,
    ...getCommonConfig(),
  });

  const dispatchEdit = async (editBody, fieldUpdated) => {
    if (element?.isImported) {
      return dispatch(moveImportedElementToCategory({ ...editBody }));
    }

    if (isDuplicated) {
      return dispatch(
        editElementSerie({
          ...editBody,
          fieldUpdated,
          type: type === "all" ? "all" : "one",
        })
      );
    }

    return dispatch(editElement({ ...editBody }));
  };

  const getSuccessMessage = () => {
    let prefix = "Écriture";

    if (isDuplicated) {
      if (type === "all") {
        prefix = "La série ";
      } else {
        prefix = "L'écriture";
      }
    }

    return `${prefix} à été modifiée avec succès`;
  };

  const handleCategoryExpansion = (values) => {
    if (!isDuplicated) {
      const categoryId = values.categoryId;
      if (!expandedCategories.includes(categoryId)) {
        dispatch(expandCategories({ key: categoryId }));
      }
      if (!expandedCategories.includes(`${categoryId}-racine`)) {
        dispatch(expandCategories({ key: `${categoryId}-racine` }));
      }
    }
  };

  const handleModalClose = () => {
    if (
      submitButtonState === "validate" ||
      submitButtonStateRef.current === "validate"
    ) {
      dispatch(closeModal("add-writing-expression"));
    }
  };

  const handleCreateSubmission = async (values, setSubmitting, resetForm) => {
    const resolvedAmount = resolveAmountValue(values?.amount);
    if (resolvedAmount === null) {
      setSubmitting(false);
      return;
    }

    const categoryToSend = findById(values.categoryId, categoriesForSelect);
    const datesToSend = getDatesToSend(values, categoryToSend);
    const isPlanCompta = location.pathname.includes("compta-plan") || reellePopup?.isPlancompta;
    const categoryIdToUse = values.categoryId || categoryId;
    const payload = {
      ...getCommonConfig(),
      categoryId: categoryIdToUse,
      planComptableId: isPlanCompta ? values.planComptableId : null,
      isPlancompta: isPlanCompta,
      values: {
        ...values,
        designation: values.designation,
        amount: String(resolvedAmount),
        ...datesToSend,
        type: values.type,
        status: values.status,
      },
      expandedCategories,
    };
    await dispatch(createElements(payload)).then((res) => {
      if (res?.meta?.requestStatus === "rejected") {
        displayValidationErrors(res?.errors || res?.payload);
        return;
      }

      setSubmitting(false);

      message.success("Écriture à été ajoutée avec succès");

      if (submitButtonState === "validate") {
        dispatch(closeModal("add-writing-expression"));
      }

      if (!expandedCategories.includes(categoryIdToUse)) {
        dispatch(expandCategories({ key: categoryIdToUse }));
      }
      if (!expandedCategories.includes(`${categoryIdToUse}-racine`)) {
        dispatch(expandCategories({ key: `${categoryIdToUse}-racine` }));
      }

      resetForm();
    });
  };

  const handleVentilationSubmission = async (values, setSubmitting, resetForm) => {
    if (element?.isImported) {
      setSubmitting(false);
      displayValidationErrors({
        message: "La ventilation n'est pas disponible pour les écritures importées",
      });
      return;
    }

    const resolvedAmount = resolveAmountValue(values?.amount);
    if (resolvedAmount === null) {
      setSubmitting(false);
      return;
    }

    const initialAmount = resolveAmountValue(
      element?.amount?.toString?.() ?? String(element?.amount ?? "")
    );
    if (initialAmount === null) {
      setSubmitting(false);
      displayValidationErrors({ message: "Montant initial invalide" });
      return;
    }

    const remainingAmount = initialAmount - resolvedAmount;
    if (roundAmount(remainingAmount) === 0) {
      setSubmitting(false);
      displayValidationErrors({
        message:
          "Le montant ventilé doit être différent du montant initial afin de conserver un reste sur l'écriture d'origine",
      });
      return;
    }

    const categoryToSend =
      findById(values.categoryId, categoriesForSelect) ||
      element?.category ||
      { isSpecial: 0 };
    const datesToSend = getDatesToSend(values, categoryToSend);

    const createLiaison = submitButtonStateRef.current === "submit";
    const isPlanCompta =
      location.pathname.includes("compta-plan") || reellePopup?.isPlancompta;
    const categoryIdToUse = values.categoryId || element?.categoryId;

    const payload = {
      ...getCommonConfig(),
      elementId: element?.id,
      projectId,
      createLiaison,
      isPlancompta: isPlanCompta,
      values: {
        designation: values.designation,
        amount: String(resolvedAmount),
        ...datesToSend,
        type: values.type || element?.type,
        status: values.status || element?.status,
        categoryId: categoryIdToUse,
        accountTresoId: values.accountTresoId || element?.accountTresoId,
        planComptableId: values.planComptableId,
      },
    };

    const res = await dispatch(ventilateElement(payload));
    if (res?.meta?.requestStatus === "rejected") {
      setSubmitting(false);
      displayValidationErrors(res?.errors || res?.payload);
      return;
    }

    setSubmitting(false);
    message.success(
      createLiaison
        ? "Écriture ventilée et liaison créée avec succès"
        : "Écriture ventilée avec succès"
    );
    dispatch(closeModal("add-writing-expression"));
    resetForm();
  };

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      try {
        await WritingSchema.validate(values, { abortEarly: false });
      } catch (schemaErr) {}
      if (
        values.dateTreso &&
        !validateDateNotLocked(values.dateTreso, "treso", "dateTreso")
      ) {
        // Ventilation may reuse locked source dates — skip lock block for ventilation
        if (!isVentilation) {
          setSubmitting(false);
          return;
        }
      }

      if (
        values.dateCompta &&
        !validateDateNotLocked(values.dateCompta, "compta", "dateCompta")
      ) {
        if (!isVentilation) {
          setSubmitting(false);
          return;
        }
      }

      if (isVentilation) {
        await handleVentilationSubmission(values, setSubmitting, resetForm);
      } else if (edit && !isCreateByDuplication) {
        await handleEditSubmission(values, setSubmitting, resetForm);
      } else {
        await handleCreateSubmission(values, setSubmitting, resetForm);
      }
    } catch (error) {
      setSubmitting(false);
      displayValidationErrors(error);
    }
  };

  const extractTextFromReactElement = (element) => {
    if (typeof element === 'string' || typeof element === 'number') {
      return String(element);
    }
    
    if (!element || typeof element !== 'object') {
      return '';
    }
    
    if (element.props?.children) {
      const children = element.props.children;
      if (Array.isArray(children)) {
        return children.map(extractTextFromReactElement).join(' ');
      } else {
        return extractTextFromReactElement(children);
      }
    }
    
    return '';
  };

  const filterTreeNode = (searchValue, node) => {
    if (!searchValue) return true;

    const title = node.title?.props?.children;
    if (!title) return false;

    let titleToCheck = Array.isArray(title) ? title[0] : title;
    
    if (typeof titleToCheck !== 'string') {
      titleToCheck = extractTextFromReactElement(titleToCheck);
    }

    if (typeof titleToCheck !== 'string' || !titleToCheck) {
      return false;
    }

    return titleToCheck.toLowerCase().includes(searchValue.toLowerCase());
  };

  const LINKED_ELEMENT_NOTICE = "Écriture liée";

  const getLabelNotice = () => {
    if (isElementLinked && !isCreateByDuplication) {
      return (
        <div className="label-notice-wrapper">
          <img src={link} alt="linked-element" />
          {LINKED_ELEMENT_NOTICE}
        </div>
      );
    }
    return null;
  };

  const disableBasedOnComptaMode =
    edit &&
    !isCreateByDuplication &&
    checkIfIsLocked(initialValues, "compta", project);

  const disableBasedOnTresoMode =
    edit &&
    !isCreateByDuplication &&
    checkIfIsLocked(initialValues, "treso", project);

  const disableBasedOnMode =
    disableBasedOnComptaMode || disableBasedOnTresoMode;

  // Ventilation restrictions
  const isVentilationPointedOrLinked =
    !!isVentilation &&
    (element?.status === "pointé" || !!element?.linkedGroupId);
  const isVentilationLockedInResultat =
    !!isVentilation && checkIfIsLocked(element, "compta", project);

  return (
    <>
      <p className="description">* champs obligatoires.</p>
      <Formik
        key={`writing-form-${isVentilation ? "vent" : "edit"}-${
          element?.id ?? "new"
        }-${categoryId ?? selectedCategory ?? "none"}`}
        initialValues={initialValues}
        validationSchema={WritingSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          values,
          isSubmitting,
          setSubmitting,
          handleChange,
          handleBlur,
          setFieldValue,
          errors,
          touched,
        }) => {
          const specialCategory = values.isSpecial;
          const statusesArray =
            values.type === "simulation" ? ONLY_SIMUL : TRESORY_STATUS;
          const labelDifference = getLabelForDateDifference(
            "compta",
            values.dateTreso,
            values.dateCompta
          );
          const labelDifferenceCompta = getLabelForDateDifference(
            "treso",
            values.dateTreso,
            values.dateCompta
          );

          const matchingLabelTreso = labelDifference["matchingLabel"];
          const monthDifferenceTreso = labelDifference["monthDifference"];
          const matchingLabelCompta = labelDifferenceCompta["matchingLabel"];
          const monthDifferenceCompta =
            labelDifferenceCompta["monthDifference"];
          isSpecialState(values?.isSpecial);
          const hasImportedLabelChanged =
            edit &&
            element?.isImported &&
            !isImportedAfterDnd &&
            String(values?.designation || "").trim() !==
              String(element?.designation || "").trim() &&
            String(values?.designation || "").trim() !== "";
          const initialVentilationAmount = resolveAmountValue(
            element?.amount?.toString?.() ?? String(element?.amount ?? "")
          );
          const enteredVentilationAmount = resolveAmountValue(values?.amount);
          const remainingVentilationAmount =
            isVentilation &&
            initialVentilationAmount !== null &&
            enteredVentilationAmount !== null
              ? roundAmount(initialVentilationAmount - enteredVentilationAmount)
              : null;

          const showLinkedAmountInfo =
            !!edit &&
            !!isElementLinked &&
            !isVentilation &&
            !isCreateByDuplication &&
            !element?.isImported;
          const initialLinkedAmount = showLinkedAmountInfo
            ? resolveAmountValue(
                element?.linkedGroupSum?.toString?.() ??
                  String(element?.linkedGroupSum ?? "")
              )
            : null;
          const originalLinkedElementAmount = showLinkedAmountInfo
            ? resolveAmountValue(
                element?.amount?.toString?.() ?? String(element?.amount ?? "")
              )
            : null;
          const enteredLinkedElementAmount = showLinkedAmountInfo
            ? resolveAmountValue(values?.amount)
            : null;
          const amountChangedForLink =
            showLinkedAmountInfo &&
            originalLinkedElementAmount !== null &&
            enteredLinkedElementAmount !== null &&
            roundAmount(originalLinkedElementAmount) !==
              roundAmount(enteredLinkedElementAmount);
          const newLinkedAmount =
            amountChangedForLink && initialLinkedAmount !== null
              ? roundAmount(
                  initialLinkedAmount -
                    originalLinkedElementAmount +
                    enteredLinkedElementAmount
                )
              : null;
          const normalizeComparableDate = (value) => {
            if (!value) return null;
            const parsed = dayjs(value);
            return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
          };
          const canCreateVentilationLink =
            isVentilation &&
            normalizeComparableDate(values?.dateTreso) ===
              normalizeComparableDate(element?.dateTreso) &&
            String(values?.accountTresoId ?? "") ===
              String(element?.accountTresoId ?? "") &&
            String(values?.status ?? "") === String(element?.status ?? "");
          return (
            <Form
              className="form-container"
              autoComplete="off"
              preserve={false}
            >
              <Tooltip
                title={values.designation || null}
                placement="topRight"
                mouseEnterDelay={0.4}
              >
                <div>
                  <Field
                    component={CustomInput}
                    value={values.designation}
                    label="Libellé*"
                    name="designation"
                    type="text"
                    className={getFieldClasses(values.designation)}
                    placeholder="Saisir un libellé"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onFocus={() => handleInputFocus("designation")}
                  />
                </div>
              </Tooltip>
              {hasImportedLabelChanged && (
                <div className="save-imported-label-action">
                  <button
                    type="button"
                    className="save-imported-label-btn"
                    disabled={isSubmitting}
                    onClick={() =>
                      handleSaveImportedLabel(values, setSubmitting)
                    }
                    style={{
                      border: "1.5px solid #111",
                      background: "#111",
                      color: "#fff",
                      borderRadius: "999px",
                      padding: "6px 14px",
                      fontSize: "1.3rem",
                      fontWeight: 600,
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      lineHeight: 1.2,
                      textDecoration: "none",
                      opacity: isSubmitting ? 0.55 : 1,
                    }}
                  >
                    {isSubmitting
                      ? "Enregistrement..."
                      : "Enregistrer la modification du libellé"}
                  </button>
                </div>
              )}
              <Field
                component={CustomInput}
                labelNotice={getLabelNotice()}
                value={values.amount}
                label="Montant*"
                name="amount"
                type="string"
                className={getFieldClasses(values.amount)}
                placeholder="Saisir un montant (ex: 10+5)"
                onBlur={(e) => {
                  handleBlur(e);
                  const evaluated = evaluateAmountExpression(values.amount);
                  if (evaluated.ok) {
                    setFieldValue("amount", formatResolvedAmount(evaluated.value));
                  }
                }}
                onChange={handleChange}
                onFocus={() => handleInputFocus("amount")}
                disabled={
                  disableBasedOnMode || element?.isImported
                }
              />
              {isVentilation && initialVentilationAmount !== null && (
                <div className="ventilation-initial-amount">
                  Montant initial de l&apos;écriture à ventiler :{" "}
                  {formatResolvedAmount(initialVentilationAmount)}
                </div>
              )}
              {isVentilation && remainingVentilationAmount !== null && (
                <div className="ventilation-remaining-amount">
                  Nouveau montant aprés ventilation :{" "}
                  {formatResolvedAmount(remainingVentilationAmount)}
                </div>
              )}
              {showLinkedAmountInfo && initialLinkedAmount !== null && (
                <div className="ventilation-initial-amount">
                  Montant initial de la liaison :{" "}
                  {formatResolvedAmount(initialLinkedAmount)}
                </div>
              )}
              {showLinkedAmountInfo && newLinkedAmount !== null && (
                <div className="ventilation-remaining-amount">
                  Nouveau montant de la liaison :{" "}
                  {formatResolvedAmount(newLinkedAmount)}
                </div>
              )}
              <div className="wrapper-select">
                <label htmlFor="type" className="input-text">
                  Catégorie*
                </label>
                <TreeSelect
                  key={
                    isImported
                      ? `category-tree-${element?.id ?? "new"}-${categoryTreeInstanceKey}`
                      : `category-tree-${element?.id ?? "new"}`
                  }
                  showSearch
                  filterTreeNode={filterTreeNode}
                  bordered={false}
                  name="category"
                  value={values.categoryId}
                  dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                  placeholder="Choisir une catégorie"
                  allowClear
                  disabled={isVentilationLockedInResultat}
                  className={getFieldClasses(values.categoryId, true)}
                  onBlur={handleBlur}
                  onChange={(value) => {
                    handelChangeCategories(setFieldValue, value, values);
                  }}
                  onFocus={() => handleInputFocus("category")}
                  treeData={finalCategories}
                  treeDefaultExpandedKeys={
                    isImported
                      ? undefined
                      : [`${values.categoryId}`]
                  }
                  {...(isImported
                    ? {
                        treeExpandedKeys: categoryTreeExpandedKeys,
                        onTreeExpand: setCategoryTreeExpandedKeys,
                        searchValue: categoryTreeSearchValue,
                        onSearch: setCategoryTreeSearchValue,
                      }
                    : {})}
                />

                {errors.categoryId && touched.categoryId ? (
                  <div className="error-displayer-form-modal">
                    {errors.categoryId}
                  </div>
                ) : null}
              </div>
              {isDuplicated && type == "all" && (
                <CustomDateSelect
                  style={
                    TRESO_MODE
                      ? { width: "48%" }
                      : { width: "48%", marginLeft: "51%" }
                  }
                  label={"Décaler la série de  *"}
                  displayValue={
                    displayDecalageValue !== null
                      ? displayDecalageValue
                      : "Ne pas décaler"
                  }
                  name="decalageSerie"
                  value={TRESO_MODE ? values?.dateTreso : values?.dateCompta}
                  options={DECALAGE_SERIE}
                  condition={true}
                  specialCategory={specialCategory}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  onFocus={() => handleInputFocus("dateTreso")}
                  onChange={(changedValue) => {
                    setDisplayDecalageValue(changedValue);
                    if (isZero(changedValue)) {
                      setFieldValue("decalageSerie", undefined);
                    } else {
                      setFieldValue("decalageSerie", changedValue?.toString());
                      // Keep engagement/treso offset paired when series shift changes
                      if (values.decalageDate == null) {
                        if (displayValueCompta !== null && TRESO_MODE) {
                          setFieldValue(
                            "decalageDate",
                            (-parseFloat(displayValueCompta)).toString()
                          );
                        } else if (displayValueTreso !== null && COMPTA_MODE) {
                          setFieldValue(
                            "decalageDate",
                            displayValueTreso.toString()
                          );
                        } else if (values.dateTreso && values.dateCompta) {
                          const monthDiff = dayjs(values.dateTreso).diff(
                            dayjs(values.dateCompta),
                            "month"
                          );
                          const derived = String(
                            TRESO_MODE ? -monthDiff : monthDiff
                          );
                          if (["-2", "-1", "0", "1", "2"].includes(derived)) {
                            setFieldValue("decalageDate", derived);
                          }
                        }
                      }
                    }
                  }}
                />
              )}
              <div
                className={`${
                  COMPTA_MODE && specialCategory ? "special-form-container" : ""
                }`}
              >
                <div className="element-wrapper">
                  {isDuplicated && type == "all" ? (
                    <CustomDateSelect
                      style={{ width: "48%" }}
                      displayValue={getDisplayValueTreso(
                        displayValueTreso,
                        TRESO_MODE,
                        values,
                        getLastDayLabel,
                        matchingLabelTreso
                      )}
                      name="dateTreso"
                      value={values?.dateTreso}
                      options={optionsTreso}
                      specialCategory={specialCategory}
                      errors={errors}
                      touched={touched}
                      onChange={(changedValue) => {
                        handleChangeTresoDate(
                          changedValue,
                          setFieldValue,
                          setDisplayValueTreso,
                          TRESO_MODE,
                          COMPTA_MODE,
                          selectedDateTreso,
                          setSelectedDateTreso,
                          monthDifferenceCompta,
                          setSelectedDateCompta,
                          selectedDateCompta,
                          specialCategory,
                          edit,
                          displayValueCompta
                        );
                        // Keep series shift paired when treso offset changes (compta mode)
                        if (
                          COMPTA_MODE &&
                          displayDecalageValue !== null &&
                          !isZero(displayDecalageValue) &&
                          values.decalageSerie == null
                        ) {
                          setFieldValue(
                            "decalageSerie",
                            displayDecalageValue.toString()
                          );
                        }
                      }}
                      onBlur={handleBlur}
                      onFocus={() => handleInputFocus("dateTreso")}
                      label={
                        TRESO_MODE
                          ? "Jour du mois de trésorerie*"
                          : "Date de trésorerie (décalage)*"
                      }
                      condition={TRESO_MODE}
                    />
                  ) : (
                    ((TRESO_MODE && specialCategory) || !specialCategory) && (
                      <CustomDatePicker
                        errors={errors}
                        touched={touched}
                        labelNotice={getLabelNotice()}
                        disabled={
                          disableBasedOnTresoMode ||
                          element?.isImported ||
                          isVentilationPointedOrLinked
                        }
                        style={{ width: "100%" }}
                        label="Date de trésorerie*"
                        shouldBeHalfSize={specialCategory}
                        name="dateTreso"
                        values={values?.dateTreso}
                        onFocus={() => handleInputFocus("dateTreso")}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        dropdownClassName="custom-datepicker-dropdown"
                      />
                    )
                  )}
                  {isDuplicated && type == "all" ? (
                    <CustomDateSelect
                      style={{ width: "48%" }}
                      name="dateCompta"
                      displayValue={getDispalyedValueCompta({
                        displayValueCompta,
                        TRESO_MODE,
                        values,
                        getLastDayLabel,
                        matchingLabelCompta,
                      })}
                      labelNotice={getLabelNotice()}
                      value={values?.dateCompta}
                      specialCategory={specialCategory}
                      options={optionsCompta}
                      errors={errors}
                      touched={touched}
                      onChange={(changedValue) => {
                        handleChangeComptaDate(
                          changedValue,
                          setFieldValue,
                          monthDifferenceTreso,
                          setDisplayValueCompta,
                          TRESO_MODE,
                          COMPTA_MODE,
                          selectedDateTreso,
                          setSelectedDateCompta,
                          selectedDateCompta,
                          setSelectedDateTreso,
                          specialCategory,
                          edit,
                          displayValueTreso
                        );
                        // Keep series shift paired when engagement offset changes
                        if (
                          displayDecalageValue !== null &&
                          !isZero(displayDecalageValue) &&
                          values.decalageSerie == null
                        ) {
                          setFieldValue(
                            "decalageSerie",
                            displayDecalageValue.toString()
                          );
                        }
                      }}
                      onBlur={handleBlur}
                      onFocus={() => handleInputFocus("dateCompta")}
                      label={
                        COMPTA_MODE
                          ? "Jour du mois d'engagement*"
                          : "Date d'engagement (décalage)*"
                      }
                      condition={COMPTA_MODE}
                    />
                  ) : (
                    ((COMPTA_MODE && specialCategory) || !specialCategory) && (
                      <>
                        <CustomDatePicker
                          label="Date d'engagement [résultat]*"
                          name="dateCompta"
                          values={values.dateCompta}
                          onFocus={() => handleInputFocus("dateCompta")}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          touched={touched}
                          disabled={
                            disableBasedOnComptaMode ||
                            isVentilationLockedInResultat
                          }
                          labelNotice={
                            isImported &&
                            values.dateTreso &&
                            !disableBasedOnComptaMode &&
                            !isVentilationLockedInResultat ? (
                              <Tooltip title={'Recopier la "date de trésorerie" vers "date d\'engagement"'}>
                                <button
                                  type="button"
                                  className="copy-date-button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (values.dateTreso) {
                                      setFieldValue("dateCompta", values.dateTreso);
                                    }
                                  }}
                                >
                                  <img
                                    src={duplicateWriting}
                                    alt="Utiliser la date tréso"
                                    className="duplicate-img"
                                  />
                                </button>
                              </Tooltip>
                            ) : null
                          }
                        />
                      </>
                    )
                  )}
                </div>
                <div className="element-wrapper">
                  {specialCategory && COMPTA_MODE ? null : (
                    <CustomDropdown
                      onBlur={handleBlur}
                      onChange={setFieldValue}
                      values={values}
                      projectId={projectId}
                      labelNotice={getLabelNotice()}
                      disabled={
                        disableBasedOnTresoMode ||
                        element?.isImported ||
                        isVentilationPointedOrLinked
                      }
                    />
                  )}
                  {!(specialCategory && TRESO_MODE) && (
                    <div className="wrapper-select-plan">
                      <label htmlFor="type" className="input-text">
                        Plan comptable (facultatif)
                      </label>
                      <TreeSelect
                        showSearch
                        bordered={false}
                        filterTreeNode={filterTreeNode}
                        name="planComptableId"
                        value={values.planComptableId}
                        dropdownStyle={{
                          maxHeight: 400,
                          width: "450px",
                          overflow: "auto",
                        }}
                        popupClassName="plan-comptable-dropdown"
                        placeholder="Choisir un plan comptable"
                        allowClear
                        disabled={isVentilationLockedInResultat}
                        className={getFieldClasses(
                          values.planComptableId,
                          true
                        )}
                        onBlur={handleBlur}
                        onChange={(value) => {
                          setFieldValue("planComptableId", value);
                        }}
                        onFocus={() => handleInputFocus("planComptableId")}
                        treeData={finalPlancompta}
                        treeDefaultExpandedKeys={[`${values.planComptableId}`]}
                      />

                      {errors.planComptableId ? (
                        <div className="error-displayer-form-modal">
                          {errors.planComptableId}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
              <div className="element-wrapper">
                <CustomSelect
                  label="Statut de trésorerie*"
                  name="status"
                  errors={errors}
                  labelNotice={getLabelNotice()}
                  touched={touched}
                  codeColor={values?.accountColor}
                  disabledOption={isDuplicated && type == "all" ? "pointé" : ""}
                  value={
                    values.type === "simulation" ? "simulation" : values?.status
                  }
                  disabled={
                    type === "simulation" ||
                    (COMPTA_MODE && specialCategory) ||
                    (TRESO_MODE && values.type === "simulation") ||
                    disableBasedOnTresoMode ||
                    element?.isImported ||
                    isVentilationPointedOrLinked
                  }
                  onChange={setFieldValue}
                  options={statusesArray}
                  condition={
                    (TRESO_MODE && specialCategory) || !specialCategory
                  }
                  showCodeColor
                  showIcon
                  isSpecialTreso={TRESO_MODE && specialCategory}
                  code={values?.accountCode}
                />

                <CustomSelect
                    errors={errors}
                    touched={touched}
                    label="Type d'écriture*"
                    name="type"
                    value={values?.type}
                    disabled={
                      (!edit && isAction) ||
                      disableBasedOnMode ||
                      element?.isImported ||
                      isVentilationPointedOrLinked ||
                      isVentilationLockedInResultat
                    }
                    isSpecialCompta={COMPTA_MODE && specialCategory}
                    onChange={(_, value) => {
                      setFieldValue("type", value);

                      if (value !== "simulation") return;
                      if (!COMPTA_MODE) {
                        setFieldValue("status", "prévisionnel");
                        return;
                      }

                      let status;
                      if (edit) {
                        status = values.isSpecial ? "pointé" : "prévisionnel";
                      } else {
                        status = !values.isSpecial ? "prévisionnel" : "pointé";
                      }
                      setFieldValue("status", status);
                    }}
                    options={WRITING_TYPES}
                    disabledOption="soldé"
                    condition={
                      (COMPTA_MODE && specialCategory) ||
                      (TRESO_MODE && specialCategory) ||
                      !specialCategory
                    }
                    showIcon
                    code={values?.accountCode}
                  />
              </div>

              <div className="submit-button-wrapper">
                <Button
                  text={isSubmitting ? "envoi en cours" : "Valider"}
                  type="submit"
                  disabled={
                    isSubmitting ||
                    (isVentilation &&
                      (element?.status === "pointé" ||
                        !!element?.linkedGroupId))
                  }
                  color="primary"
                  className="submit-button"
                  allWidth
                  onClick={async (e) => {
                    const disableValidate =
                      isVentilation &&
                      (element?.status === "pointé" ||
                        !!element?.linkedGroupId);
                    if (disableValidate) {
                      e?.preventDefault?.();
                      return;
                    }
                    try {
                      await WritingSchema.validate(values, {
                        abortEarly: false,
                      });
                    } catch (schemaErr) {}
                    handleSubmitButtonState("validate");
                  }}
                />
                <Button
                  text="Annuler"
                  color="primary"
                  className="reset-button"
                  allWidth
                  onClick={(e) => {
                    aiNextCancelledRef.current = true;
                    message.destroy("ai-next-imported-categorization");
                    onCancel();
                    e.preventDefault();
                  }}
                />
              </div>
              <div className="validate">
                {!edit &&
                  !(isVentilation && specialCategory && COMPTA_MODE) && (
                  <Button
                    type="submit"
                    text={
                      isSubmitting
                        ? "envoi en cours"
                        : isVentilation
                          ? "Valider et créer une liaison"
                          : "Valider et ajouter une nouvelle écriture"
                    }
                    disabled={
                      isSubmitting ||
                      (isVentilation && !canCreateVentilationLink)
                    }
                    color="primary"
                    className="validate-button"
                    allWidth
                    onClick={(e) => {
                      if (isVentilation && !canCreateVentilationLink) {
                        e?.preventDefault?.();
                        return;
                      }
                      handleSubmitButtonState("submit");
                    }}
                  />
                )}
                {edit && element?.isImported && !isImportedAfterDnd && (
                  <>
                    <Button
                      type="submit"
                      text={
                        isSubmitting ? "envoi en cours" : "Valider et ventiler"
                      }
                      disabled={isSubmitting}
                      color="primary"
                      className="validate-button"
                      allWidth
                      onClick={async () => {
                        try {
                          await WritingSchema.validate(values, {
                            abortEarly: false,
                          });
                        } catch (schemaErr) {}
                        handleSubmitButtonState("validateAndVentilate");
                      }}
                    />
                    <Button
                      type="submit"
                      text={
                        isSubmitting
                          ? "envoi en cours"
                          : "Valider et classer l'écriture suivante"
                      }
                      disabled={isSubmitting}
                      color="primary"
                      className="validate-button"
                      allWidth
                      onClick={async () => {
                        try {
                          await WritingSchema.validate(values, {
                            abortEarly: false,
                          });
                        } catch (schemaErr) {}
                        handleSubmitButtonState("validateAndNext");
                      }}
                    />
                  </>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default AddWritingForm;
